import { useCallback, useMemo, useState } from 'react'

import type { OnlyFansMessage } from '@api'
import { format } from 'date-fns'
import { useUnit } from 'effector-react'

import {
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { $lastActiveChatMessage } from '@/models/chattingUI/chats'
import { $fanId } from '@/models/chattingUI/chats/chats.ts'
import {
  toggleMessageLikeFx,
  useMessageLikeLoading,
} from '@/models/chattingUI/messages/messages.ts'
import {
  pinMessageFx,
  unpinMessageFx,
} from '@/models/chattingUI/messages/pinnedMessages.ts'
import {
  $chatterLanguage,
  $isAutoTranslationsEnabled,
  $isTranslatorEnabled,
  toggleTranslation,
  useMessageTranslation,
} from '@/models/chattingUI/messages/translations.ts'
import { $creatorId } from '@/models/creators.ts'
import PinIcon from '@/modules/chatting/icons/icon-pin.svg'
import ReadMessageArrowIcon from '@/modules/chatting/icons/read-message-arrow.svg'
import UnreadMessageArrowIcon from '@/modules/chatting/icons/unread-message-arrow.svg'
import { useChatting } from '@/modules/chatting/state/chatting-provider.tsx'
import type { Message } from '@/modules/chatting/types.ts'
import { UnsendDropdownItem } from '@/modules/chatting/ui/components/ChatMessageGroup/UnsendDropdownItem.tsx'
import { classNames } from '@/utils/helpers'

import { ChatMessageFromPartner } from './ChatMessageFromPartner.tsx'
import { ChatMessageFromUser } from './ChatMessageFromUser.tsx'

export interface MessageProps {
  message: Message
  highlightMessage: (messageId: number) => void
  prevMessage?: Message
  nextMessage?: Message
  hasMessage: (id: number) => boolean
  searchedKeyword?: string
  onClickReply?: () => void
}

export const ChatMessage = (props: MessageProps) => {
  const { message, highlightMessage, prevMessage, nextMessage, onClickReply } =
    props
  const fanId = useUnit($fanId)
  const lastActiveChatMessage = useUnit($lastActiveChatMessage)

  const isAutoTranslationsEnabled = useUnit($isAutoTranslationsEnabled)
  const isTranslatorEnabled = useUnit($isTranslatorEnabled)
  const chatterLanguage = useUnit($chatterLanguage)
  const creatorId = useUnit($creatorId)

  const { setReplyToMessage } = useChatting()
  const { id, createdAt, fromUser } = message
  const isLikeMessageLoading = useMessageLikeLoading(id)
  const isFromPartner = fromUser.id === fanId

  const formattedDate = useMemo(
    () => format(new Date(createdAt), 'hh:mm a'),
    [createdAt],
  )

  const isMessageRead = useCallback(
    (message: Message) => {
      if (message.fromUser.id === creatorId) {
        return lastActiveChatMessage?.isReadByCreator || message.isReadByCreator
      }

      return lastActiveChatMessage?.isReadByFan || message.isReadByFan
    },
    [
      creatorId,
      lastActiveChatMessage?.isReadByCreator,
      lastActiveChatMessage?.isReadByFan,
    ],
  )

  const messagesBelongTogether = useCallback(
    (messageA: Message | undefined, messageB: Message | undefined) => {
      return (
        !!messageA &&
        !!messageB &&
        messageA.fromUser.id === messageB.fromUser.id &&
        messageA.authorName === messageB.authorName &&
        isMessageRead(messageA) === isMessageRead(messageB) &&
        Math.abs(
          new Date(messageA.createdAt).getTime() -
            new Date(messageB.createdAt).getTime(),
        ) < 60000 &&
        messageA.isFree &&
        messageB.isFree &&
        !messageA.isLiked &&
        !messageB.isLiked &&
        !messageA.isPinned &&
        !messageB.isPinned
      )
    },
    [isMessageRead],
  )
  const belongsToPrevMessage = useMemo(
    () => messagesBelongTogether(message, prevMessage),
    [message, messagesBelongTogether, prevMessage],
  )
  const belongsToNextMessage = useMemo(
    () => messagesBelongTogether(message, nextMessage),
    [message, messagesBelongTogether, nextMessage],
  )
  const isPinMessageLoading = useUnit(pinMessageFx.pending)
  const isUnpinMessageLoading = useUnit(unpinMessageFx.pending)
  const isPinned = message.isPinned
  const canBePinned = message.canBePinned

  const handlePinClick = useCallback(() => {
    if (isPinned) {
      unpinMessageFx(id)
    } else {
      pinMessageFx(id)
    }
  }, [isPinned, id])

  const handleReplyClick = useCallback(() => {
    setReplyToMessage(message)
    onClickReply?.()
  }, [message, setReplyToMessage, onClickReply])

  const handleLikeClick = useCallback(() => {
    toggleMessageLikeFx(message)
  }, [message])

  const translation = useMessageTranslation(message.id)

  const dropdownContent = useMemo(
    () => (
      <DropdownMenuContent className="z-100 p-1.5">
        {!!chatterLanguage && !!message.text && isTranslatorEnabled && (
          <DropdownMenuItem
            onClick={() => toggleTranslation(message.id)}
            disabled={
              isTranslatorEnabled &&
              isAutoTranslationsEnabled &&
              !!translation?.text
            }
          >
            {translation?.text ? 'Undo Translate' : 'Translate'}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={handleReplyClick}>Reply</DropdownMenuItem>
        {isFromPartner && (
          <DropdownMenuItem
            onClick={handleLikeClick}
            disabled={isLikeMessageLoading}
          >
            {message.isLiked ? 'Unlike' : 'Like'}
          </DropdownMenuItem>
        )}
        {!isFromPartner && <UnsendDropdownItem message={message} />}
        {canBePinned && (
          <DropdownMenuItem
            disabled={isPinMessageLoading || isUnpinMessageLoading}
            onClick={handlePinClick}
          >
            {isPinned ? 'Unpin' : 'Pin'}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    ),
    [
      canBePinned,
      chatterLanguage,
      handleLikeClick,
      handlePinClick,
      handleReplyClick,
      isAutoTranslationsEnabled,
      isFromPartner,
      isLikeMessageLoading,
      isPinMessageLoading,
      isPinned,
      isTranslatorEnabled,
      isUnpinMessageLoading,
      message,
      translation,
    ],
  )

  const messageId = `chatting-message-${id}`

  const handleViewMessageClick = useCallback(
    (m?: OnlyFansMessage) => {
      if (m) {
        highlightMessage(m.id)
      }
    },
    [highlightMessage],
  )

  const messageTag = (
    <div
      className={classNames(
        'mt-1 flex w-max items-center gap-2 overflow-hidden whitespace-nowrap text-sm',
        { 'mt-6': !message.isFree },
      )}
    >
      {!belongsToNextMessage && (
        <>
          {message.isPinned && (
            <span>
              <PinIcon className="text-secondary-300 h-3.5 w-3.5" />
            </span>
          )}
          {message.isLiked && (
            // eslint-disable-next-line jsx-a11y/accessible-emoji
            <Tooltip
              content="Unlike"
              asChild
            >
              <span
                className="cursor-pointer"
                onClick={handleLikeClick}
              >
                ❤️
              </span>
            </Tooltip>
          )}
          {!isFromPartner && (
            <span className="block overflow-hidden text-ellipsis font-medium">
              {message.authorName ?? 'Unknown'}
            </span>
          )}
          <span className="text-secondary-300">{formattedDate}</span>
          {!isFromPartner && (
            <div className="text-secondary-300 shrink-0 items-end">
              {!message.isReadByFan ? (
                <UnreadMessageArrowIcon />
              ) : (
                <ReadMessageArrowIcon />
              )}
            </div>
          )}
        </>
      )}
    </div>
  )

  if (isFromPartner) {
    return (
      <ChatMessageFromPartner
        {...props}
        onViewMessageClick={handleViewMessageClick}
        dropdownContent={dropdownContent}
        belongsToPrevMessage={belongsToPrevMessage}
        messageId={messageId}
        messageTag={messageTag}
        likeMessage={handleLikeClick}
        isLikeMessageLoading={isLikeMessageLoading}
      />
    )
  }

  return (
    <ChatMessageFromUser
      {...props}
      messageId={messageId}
      onViewMessageClick={handleViewMessageClick}
      belongsToPrevMessage={belongsToPrevMessage}
      dropdownContent={dropdownContent}
      messageTag={messageTag}
      isTempMessage={message._isTempMessage}
    />
  )
}
